import React, { useMemo } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

import AdminAiAnalysisTab from "@/components/application/AdminAiAnalysisTab";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import FunderAnalysisDashboard from "@/components/underwriting/FunderAnalysisDashboard";
import type { BorrowerTransaction } from "@/hooks/useTransactions";
import {
  useAnalysisOverrideMutation,
  useAnalysisRunQuery,
  useAnalysisRunsQuery,
  useRerunStatementAnalysisMutation,
} from "@/hooks/queries/useAnalysisRuns";
import type {
  AnalysisDecision,
  AnalysisRun,
  AnalysisRunListItem,
  AnalysisRunStatement,
  AnalysisRunTransaction,
  BalanceSource,
  CompletenessStatus,
} from "@/types/analysisRun";
import type { BorrowerDetail } from "@/types/borrower";
import type { AnalysisOutput } from "@/types/underwriting";

import type { AnalysisListLinkState } from "./analysisListLink";
import ArAnalysisSection from "./ArAnalysisSection";
import CollapsibleSection from "./CollapsibleSection";
import InitialStatementCheck, { initialStatementValidation } from "./InitialStatementCheck";
import DecisionPanel from "./DecisionPanel";
import RerunAnalysisButton from "./RerunAnalysisButton";

interface Props {
  borrower: BorrowerDetail;
  borrowerId: number;
  /** Admin Support and other non-underwriting actors view analysis read-only. */
  readOnly?: boolean;
  /** The transaction list's state from the address ("Show in application"); none, no change. */
  listLink?: AnalysisListLinkState;
}

/** Runs that produced something worth rendering. */
function isCompleted(run: AnalysisRunListItem): boolean {
  return run.status === "success" || run.status === "partial";
}

const PIPELINE_ANALYSIS_ENGINES = new Set(["claude", "openrouter"]);

/**
 * Read-only path for historical deals that still have a pre-pipeline (MoneyThumb) statements decision. Pipeline runs stamp `analysis_engine` as `claude` or `openrouter`; anything else with a rendered analysis is legacy.
 */
export function hasLegacyMoneyThumbAnalysis(
  borrower: Pick<BorrowerDetail, "decisions"> | null | undefined
): boolean {
  return (borrower?.decisions ?? []).some((d) => {
    const engine = d.detailed_results?.analysis_engine;
    return (
      (d.data_source ?? "statements") === "statements" &&
      !PIPELINE_ANALYSIS_ENGINES.has(engine ?? "") &&
      !!d.detailed_results?.analysis
    );
  });
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === "string") return data;
    if (data && typeof data === "object") {
      if ("detail" in data && typeof data.detail === "string") return data.detail;
      return JSON.stringify(data);
    }
    return err.message;
  }
  return String(err);
}

/** Map run transactions to the dashboard's enriched-transaction row shape. */
function toBorrowerTransactions(
  transactions: AnalysisRunTransaction[],
  statements: AnalysisRunStatement[]
): BorrowerTransaction[] {
  const accountByStatement = new Map<number, string>(
    statements.map((s) => [s.statement_id, s.account_number])
  );
  return transactions.map((t) => {
    const amount = Number(t.amount);
    return {
      id: t.index,
      date: t.date,
      description: t.description,
      amount,
      balance: t.balance != null ? Number(t.balance) : undefined,
      transaction_type: amount >= 0 ? "credit" : "debit",
      category: t.txn_type,
      memo: t.memo || undefined,
      counterparty_name: t.counterparty || undefined,
      canonical_counterparty_name: t.canonical_counterparty || undefined,
      account_number: accountByStatement.get(t.statement_id) || undefined,
      is_mca_payment: t.txn_type === "MCA_WITHDRAWAL",
      is_revenue: t.rev === "TR",
      is_nsf: t.txn_type === "NSF_RETURN",
      override_key: t.override_key || undefined,
    };
  });
}

const RECON_BADGES: Record<AnalysisRunStatement["reconciliation_status"], string> = {
  reconciled: "bg-emerald-100 text-emerald-800",
  delta: "bg-amber-100 text-amber-800",
  no_stated_balances: "bg-muted text-muted-foreground",
  error: "bg-red-100 text-red-800",
};

const COMPLETENESS_BADGES: Record<CompletenessStatus, string> = {
  verified: "bg-emerald-100 text-emerald-800",
  delta: "bg-red-100 text-red-800",
  unavailable: "bg-muted text-muted-foreground",
  not_applicable: "bg-muted text-muted-foreground",
};

const BALANCE_SOURCE_BADGES: Record<BalanceSource, string> = {
  printed: "bg-emerald-100 text-emerald-800",
  printed_daily_summary: "bg-emerald-100 text-emerald-800",
  computed_reconciled: "bg-amber-100 text-amber-800",
  unavailable: "bg-red-100 text-red-800",
};

const BALANCE_SOURCE_LABELS: Record<BalanceSource, string> = {
  printed: "balances: printed",
  printed_daily_summary: "balances: daily summary",
  computed_reconciled: "balances: computed",
  unavailable: "balances: none reported",
};

/** How many rows we are short of (or over) what the bank printed. */
function completenessLabel(s: AnalysisRunStatement): string {
  if (s.completeness_status !== "delta") return "totals verified";
  const rows = s.credit_count_delta ?? 0;
  const dollars = s.credit_delta;
  if (rows < 0) return `missing ${Math.abs(rows)} credit(s)${dollars ? ` · ${dollars}` : ""}`;
  if (rows > 0) return `${rows} extra credit(s)${dollars ? ` · ${dollars}` : ""}`;
  return `totals off${dollars ? ` by ${dollars}` : ""}`;
}

const StatementsSummary: React.FC<{ statements: AnalysisRunStatement[] }> = ({ statements }) => (
  <CollapsibleSection title="Statement checks" defaultOpen={false}>
    <div className="flex flex-wrap gap-2">
      {statements.map((s) => (
        <div key={s.statement_id} className="rounded-md border p-2 text-xs">
          <div className="font-medium">{s.filename || `Statement ${s.statement_id}`}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-muted-foreground">
            <span>
              {s.period_start || "?"} – {s.period_end || "?"} · {s.txn_count} txns
              {s.skipped_rows > 0 && ` · ${s.skipped_rows} skipped`}
              {s.truncated && " · truncated"}
              {(s.truncation_retries ?? 0) > 0 &&
                ` · re-read in ${s.truncation_retries} page range(s)`}
              {(s.duplicate_rows_dropped ?? 0) > 0 &&
                ` · ${s.duplicate_rows_dropped ?? 0} duplicate(s) dropped`}
              {(s.sign_repairs ?? 0) > 0 && ` · ${s.sign_repairs} sign(s) corrected`}
            </span>
            <Badge variant="outline" className={RECON_BADGES[s.reconciliation_status]}>
              {s.reconciliation_status === "delta"
                ? `delta ${s.reconciliation_delta}`
                : s.reconciliation_status.replaceAll("_", " ")}
            </Badge>
            {s.completeness_status &&
              s.completeness_status !== "unavailable" &&
              s.completeness_status !== "not_applicable" && (
                <Badge variant="outline" className={COMPLETENESS_BADGES[s.completeness_status]}>
                  {completenessLabel(s)}
                </Badge>
              )}
            {s.balance_source && (
              <Badge variant="outline" className={BALANCE_SOURCE_BADGES[s.balance_source]}>
                {BALANCE_SOURCE_LABELS[s.balance_source]}
              </Badge>
            )}
          </div>
          {s.error && <div className="mt-1 text-red-700">{s.error}</div>}
        </div>
      ))}
    </div>
  </CollapsibleSection>
);

/**
 * One line while the pipeline is still transcribing, in place of the old run
 * controls card. Progress comes from the detail query, which polls while the
 * run is processing.
 */
const ProcessingLine: React.FC<{ run: AnalysisRun | null }> = ({ run }) => {
  const stage1 = run?.stage_status_json?.stage1 ?? {};
  const done = Object.values(stage1).filter((s) => s === "success" || s === "error").length;
  const total = Object.keys(stage1).length;
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Analyzing statements…
      {total > 0 && ` ${done}/${total} transcribed`}
      {run?.stage_status_json?.stage2 === "running" && " · analyzing dataset"}
    </p>
  );
};

const AiAnalysisTab: React.FC<Props> = ({ borrower, borrowerId, readOnly = false, listLink }) => {
  const runsQuery = useAnalysisRunsQuery(borrowerId);

  // Statements lane only — Plaid runs belong on Final Underwriting. The list is
  // newest-first across sources, so without this filter a finished Plaid run
  // would displace the statement analysis the reviewer expects here.
  const statementRuns = useMemo(
    () => (runsQuery.data ?? []).filter((r) => r.source === "statements"),
    [runsQuery.data]
  );
  // Show the newest finished statement run; a run that is still processing (or
  // failed) never displaces a result the reviewer can act on.
  const latestRun = statementRuns[0] ?? null;
  const displayRunId = statementRuns.find(isCompleted)?.id ?? null;
  const latestIsProcessing = latestRun?.status === "processing";

  const runQuery = useAnalysisRunQuery(displayRunId, borrowerId);
  // Path 5 writes the initial-vs-Plaid check onto the Plaid run. The tab still
  // shows the statement analysis; the check is a section under AR Analysis.
  const plaidCheckRunId = useMemo(() => {
    const completedPlaid = (runsQuery.data ?? []).filter(
      (r) => r.source === "plaid" && isCompleted(r) && r.stage_status_json?.analysis_path === "5"
    );
    const id = completedPlaid[0]?.id ?? null;
    return id != null && id !== displayRunId ? id : null;
  }, [runsQuery.data, displayRunId]);
  const plaidCheckQuery = useAnalysisRunQuery(plaidCheckRunId, borrowerId);
  // Separate detail query on the in-flight run so stage progress refreshes;
  // it is the same query when nothing has finished yet.
  const processingQuery = useAnalysisRunQuery(latestIsProcessing ? latestRun.id : null, borrowerId);
  const run: AnalysisRun | null = runQuery.data ?? null;
  const statementCheck =
    initialStatementValidation(run) ?? initialStatementValidation(plaidCheckQuery.data ?? null);
  const override = useAnalysisOverrideMutation(displayRunId, borrowerId);
  const rerun = useRerunStatementAnalysisMutation(borrowerId);

  const transactionsOverride = useMemo(
    () =>
      run && run.transactions_json.length > 0
        ? toBorrowerTransactions(run.transactions_json, run.statements_json)
        : undefined,
    [run]
  );

  const analysisOutput =
    run && run.analysis_output_json && "business_name" in run.analysis_output_json
      ? (run.analysis_output_json as AnalysisOutput)
      : null;
  const decision =
    run && run.decision_json && "status" in run.decision_json
      ? (run.decision_json as AnalysisDecision)
      : null;

  // No finished pipeline run yet: show the historical MoneyThumb decision so the
  // deal is not blank. Those figures cannot be recalculated, so the dashboard
  // is read-only even when the viewer could otherwise edit.
  const showLegacy =
    !runsQuery.isLoading && displayRunId == null && hasLegacyMoneyThumbAnalysis(borrower);
  const latestFailed = latestRun?.status === "error";
  // Override keys are content-addressed, so a link's rows usually carry over to a newer run.
  const linkedRun = listLink?.link?.run ?? null;
  const linkRunDiffers = linkedRun != null && displayRunId != null && linkedRun !== displayRunId;

  return (
    <div className="mb-6 space-y-4">
      {latestIsProcessing && <ProcessingLine run={processingQuery.data ?? null} />}

      {runsQuery.isLoading && (
        <Card className="p-6 text-center text-muted-foreground">Loading AI analysis…</Card>
      )}

      {runsQuery.isError && (
        <Card className="p-4">
          <p className="text-sm text-destructive">
            Failed to load AI analysis: {extractErrorMessage(runsQuery.error)}
          </p>
        </Card>
      )}

      {showLegacy && <AdminAiAnalysisTab borrower={borrower} readOnly />}

      {!runsQuery.isLoading &&
        !runsQuery.isError &&
        displayRunId == null &&
        !showLegacy &&
        !latestIsProcessing &&
        (latestFailed ? (
          <Card className="p-4">
            <p className="text-sm text-destructive">Run failed: {latestRun.error_message}</p>
          </Card>
        ) : (
          <Card className="p-6 text-center text-muted-foreground">
            No AI analysis yet. Upload bank statements to run one.
          </Card>
        ))}

      {displayRunId != null && runQuery.isLoading && (
        <Card className="p-6 text-center text-muted-foreground">Loading analysis results…</Card>
      )}

      {run && isCompleted(run) && (
        <>
          {analysisOutput && linkRunDiffers && (
            <p className="text-xs text-muted-foreground">
              This link was made from another analysis run. The latest finished run is shown.
            </p>
          )}
          {analysisOutput && (
            <FunderAnalysisDashboard
              analysis={analysisOutput}
              borrowerId={borrowerId}
              transactionsOverride={transactionsOverride}
              showEnrichedColumns
              startDefaultOnboarding
              readOnly={readOnly}
              listLink={listLink}
              onPersistActiveToggle={
                readOnly
                  ? undefined
                  : (targetKey, includeInLeverage) =>
                      // mutateAsync, not mutate: the dashboard applies the toggle
                      // locally first and needs the rejection to put the dot back.
                      // The shared error banner below reports it either way.
                      override.mutateAsync({
                        action: "save",
                        payload: {
                          target_type: "position",
                          target_key: targetKey,
                          field: "include_in_leverage",
                          value: includeInLeverage,
                        },
                      })
              }
              onPersistFrequency={
                readOnly
                  ? undefined
                  : (targetKey, frequency) =>
                      // Unknown is not a persisted correction: it clears any saved
                      // cadence so the next assemble/re-run can re-detect freely.
                      frequency === "unknown"
                        ? override.mutateAsync({
                            action: "clear",
                            payload: {
                              target_type: "position",
                              target_key: targetKey,
                              field: "frequency",
                            },
                          })
                        : override.mutateAsync({
                            action: "save",
                            payload: {
                              target_type: "position",
                              target_key: targetKey,
                              field: "frequency",
                              value: frequency,
                            },
                          })
              }
              onSaveTransactionOverride={
                readOnly ? undefined : (payload) => override.mutate({ action: "save", payload })
              }
            />
          )}
          {/* AR first (open by default), directly under the dashboard's balances. */}
          <ArAnalysisSection sources={run.analysis_json.ar_sources ?? []} />
          {statementCheck && <InitialStatementCheck validation={statementCheck} />}
          {analysisOutput && !readOnly && override.isError && (
            <p className="whitespace-pre-wrap text-sm text-destructive">
              Failed to save correction: {extractErrorMessage(override.error)}
            </p>
          )}
          {run.statements_json.length > 0 && <StatementsSummary statements={run.statements_json} />}
          {decision && (
            <DecisionPanel
              decision={decision}
              redFlags={run.analysis_json.red_flags ?? []}
              analystNotes={run.analysis_json.notes}
            />
          )}
        </>
      )}

      {/* Very bottom: re-run the uploaded statements. Offered
          whenever a result (or a failed run) exists; never during a run. */}
      {!readOnly &&
        !runsQuery.isLoading &&
        !showLegacy &&
        (displayRunId != null || latestFailed) && (
          <RerunAnalysisButton
            label="Re-run statement analysis"
            description="Re-reads every uploaded bank statement and replaces this analysis. Saved corrections are kept."
            onRerun={() => rerun.mutateAsync()}
            isPending={rerun.isPending}
            disabled={latestIsProcessing}
            disabledReason="An analysis is already running."
          />
        )}
    </div>
  );
};

export default AiAnalysisTab;
